import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useI18n, ACTIVE_LOCALES, type Lang } from "@/lib/i18n";
// logo served from public/logo.svg

export function Header() {
  const { lang, t } = useI18n();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Swap the leading /:lang segment while preserving the rest of the path.
  const switchTo = (next: Lang) => () => {
    const rest = pathname.replace(/^\/[a-z]{2}/, "") || "/";
    navigate({ to: `/${next}${rest === "/" ? "" : rest}` as string, replace: true });
  };

  return (
    <header className="relative z-20 border-b border-border/60 backdrop-blur-sm" style={{ backgroundColor: "rgba(228, 247, 245, 0.7)" }}>
      <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 pt-4 pb-0 md:pb-4">
        <Link
          to="/$lang"
          params={{ lang }}
          className="flex shrink-0 items-center text-foreground"
          aria-label="MadCaptains.net — home"
        >
          <img
            src="/logo.svg"
            alt="MadCaptains.net"
            className="w-auto shrink-0 h-10 md:h-12"
            style={{ minHeight: 40 }}
          />
        </Link>

        <nav
          className={`min-w-0 items-center gap-6 text-sm text-muted-foreground ${
            lang === "zh" || lang === "ar"
              ? "hidden md:flex"
              : "hidden md:flex md:order-3 md:basis-full md:justify-center md:pb-3 lg:order-none lg:basis-auto lg:justify-start lg:pb-0"
          }`}
        >
          <Link to="/$lang/stories" params={{ lang }} className="hover:text-foreground" activeProps={{ className: "text-foreground" }}>{t.nav_stories}</Link>
          <Link to="/$lang/categories" params={{ lang }} className="hover:text-foreground" activeProps={{ className: "text-foreground" }}>{t.nav_categories}</Link>
          <Link to="/$lang/learn" params={{ lang }} className="hover:text-foreground" activeProps={{ className: "text-foreground" }}>{t.nav_learn}</Link>
          <Link to="/$lang/about" params={{ lang }} className="hover:text-foreground" activeProps={{ className: "text-foreground" }}>{t.nav_about}</Link>
        </nav>

        <div className="flex items-center gap-2">
          <Link
            to="/$lang/help"
            params={{ lang }}
            className="hidden rounded-full px-3 py-1.5 text-xs font-medium text-white sm:inline-flex"
            style={{ background: "var(--red)" }}
          >
            {t.nav_help_cta}
          </Link>
          <Link
            to="/$lang/submit"
            params={{ lang }}
            className="rounded-full px-3 py-1.5 text-xs font-medium"
            style={{ background: "var(--amber)", color: "var(--ink)" }}
          >
            {t.nav_submit_cta}
          </Link>

          <div
            role="group"
            aria-label="Language"
            className="ml-1 flex items-center rounded-full border border-border bg-card/60 p-0.5 text-xs"
          >
            {ACTIVE_LOCALES.map((l) => (
              <button
                key={l}
                onClick={switchTo(l)}
                aria-pressed={lang === l}
                className={`rounded-full px-2.5 py-1 transition-colors ${lang === l ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}